import React, {
    useEffect,
    useState,
    useCallback,
    forwardRef,
    useMemo,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col, Card, Statistic, Divider, Button } from 'antd';
import { Helmet } from 'react-helmet';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
    getInsuranceStatistic,
    insuranceStatistic,
    statisticLoadingStatus,
} from '../../../redux/slices/statisticSlice';
import {
    allClients,
    getClients,
    clientsLoadingStatus,
} from '../../../redux/slices/clientsSlice';
import { getQueryParams, setQueryParams } from '../../../utils/helpFunctions';
import CustomSelect from '../../../components/CustomSelect/CustomSelect';
import Title from '../../../components/Title';
import {
    LogoSE,
    LogoNO,
    LogoFI,
    LogoDK,
    LogoIS,
    Calendar,
    Vector,
} from '../../../components/Svg/Svg';

const COUNTRIES = [
    { code: 'SE', name: 'Sweden', logo: <LogoSE /> },
    { code: 'FI', name: 'Finland', logo: <LogoFI /> },
    { code: 'NO', name: 'Norway', logo: <LogoNO /> },
    { code: 'DK', name: 'Denmark', logo: <LogoDK /> },
    { code: 'IS', name: 'Iceland', logo: <LogoIS /> },
];

export default function InsuranceStatisticPage() {
    const dispatch = useDispatch();
    const data = useSelector(insuranceStatistic);
    const loading = useSelector(statisticLoadingStatus);
    const clients = useSelector(allClients);
    const clientsLoading = useSelector(clientsLoadingStatus);

    const qp = getQueryParams();
    const now = new Date();
    const initialYear = qp.year || now.getFullYear();
    const initialMonth = qp.month || now.getMonth() + 1;
    const initialClient = qp.client || '';

    const [year, setYear] = useState(initialYear);
    const [month, setMonth] = useState(initialMonth);
    const [client, setClient] = useState(initialClient);

    const clientUid = useMemo(() => {
        if (!client) return '';

        return clients?.find((c) => c.id === client)?.client_uid;
    }, [client, clients]);

    const loadStats = useCallback(() => {
        dispatch(getInsuranceStatistic({ year, month, client: clientUid }));
    }, [dispatch, year, month, clientUid]);

    useEffect(() => {
        setQueryParams({ year, month, client });
    }, [year, month, client]);

    useEffect(() => {
        dispatch(getClients({ name: '', limit: 100 }));
    }, [dispatch]);

    useEffect(() => {
        loadStats();
    }, [loadStats]);

    const clientsOptions = clients?.reduce((acc, el) => {
        acc.push({
            id: el.id,
            value: el.id,
            label: el.name,
        });

        return acc;
    }, []);

    // Country map for rendering
    const countryMap = { SE: {}, FI: {}, NO: {}, DK: {}, IS: {} };

    if (data && data.active && Array.isArray(data.active.by_country)) {
        data.active.by_country.forEach((c) => {
            const code = c.country_code?.toUpperCase();
            if (code && countryMap[code]) {
                countryMap[code].active = { TOTAL: c.total };
            }
        });
    }

    if (data && data.new && Array.isArray(data.new.by_country)) {
        data.new.by_country.forEach((c) => {
            const code = c.country_code?.toUpperCase();
            if (code && countryMap[code]) {
                countryMap[code].new = { TOTAL: c.total };
            }
        });
    }

    const onMonthChange = (date) => {
        if (!date) return;
        setYear(date.getFullYear());
        setMonth(date.getMonth() + 1);
    };

    const isLoading = loading === 'loading' || clientsLoading === 'loading';

    return (
        <div>
            <Helmet>
                <title>Console Defentry - Insurance Statistic</title>
            </Helmet>
            <Title>Insurance Statistic</Title>

            <Row gutter={16} align="middle" style={{ marginBottom: 24 }}>
                <Col>
                    <DatePicker
                        selected={new Date(year, month - 1)}
                        onChange={onMonthChange}
                        dateFormat="MMMM yyyy"
                        customInput={<CustomDateInput />}
                        showPopperArrow={false}
                        showMonthYearPicker
                    />
                </Col>
                <Col>
                    <CustomSelect
                        name="client"
                        onChange={(option) => setClient(option.id)}
                        options={clientsOptions}
                        placeholder="Select client"
                        value={client}
                        defaultValue={null}
                    />
                </Col>
                <Col style={{ marginLeft: 'auto' }}>
                    <Button
                        className="table-top-pannel__button"
                        type="primary"
                        size="large"
                        onClick={loadStats}
                    >
                        Show
                    </Button>
                </Col>
            </Row>

            <Row gutter={16}>
                <Col span={12}>
                    <Card title="Active subscriptions" loading={isLoading}>
                        <TotalBlock
                            totals={
                                (data && data.active && data.active.totals) || {
                                    TOTAL: 0,
                                    SINGLE: 0,
                                    FAMILY: 0,
                                    BUSINESS: 0,
                                }
                            }
                        />
                    </Card>
                </Col>
                <Col span={12}>
                    <Card title="New subscriptions" loading={isLoading}>
                        <TotalBlock
                            totals={
                                (data && data.new && data.new.totals) || {
                                    TOTAL: 0,
                                    SINGLE: 0,
                                    FAMILY: 0,
                                    BUSINESS: 0,
                                }
                            }
                        />
                    </Card>
                </Col>
            </Row>

            <Divider style={{ margin: '32px 0' }}>Statistic By Country</Divider>

            <Row gutter={[16, 16]}>
                {COUNTRIES.map((country) => (
                    <Col span={12} key={country.code}>
                        <CountryCard
                            code={country.code}
                            data={countryMap[country.code] || {}}
                            loading={isLoading}
                        />
                    </Col>
                ))}
            </Row>
        </div>
    );
}

function TotalBlock({ totals }) {
    const keys = Object.keys(totals);

    return (
        <Row gutter={16}>
            {keys.map((key, idx) => (
                <Col span={24 / keys.length} key={idx}>
                    <Statistic title={key} value={totals[key] || 0} />
                </Col>
            ))}
        </Row>
    );
}

function CountryCard({ code, data, loading }) {
    // const active = data.active || {
    //     TOTAL: 0,
    //     SINGLE: 0,
    //     FAMILY: 0,
    //     BUSINESS: 0,
    // };
    // const newStats = data.new || {
    //     TOTAL: 0,
    //     SINGLE: 0,
    //     FAMILY: 0,
    //     BUSINESS: 0,
    // };
    const country = COUNTRIES.find((c) => c.code === code) || null;
    const countryName = country?.name || code;
    const countryLogo = country?.logo || null;
    const active = data.active || { TOTAL: 0 };
    const newStats = data.new || { TOTAL: 0 };

    return (
        <Card
            loading={loading}
            title={
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {countryLogo}
                    <span>{countryName}</span>
                </div>
            }
        >
            <div>
                <h4 style={{ marginBottom: 8 }}>Active</h4>
                <TotalBlock totals={active} />

                <Divider />

                <h4 style={{ marginBottom: 8 }}>New</h4>
                <TotalBlock totals={newStats} />
            </div>
        </Card>
    );
}

const CustomDateInput = forwardRef(({ value, onClick, className }, ref) => (
    <div
        className="datepicker__date-frame"
        style={{ backgroundColor: '#fff' }}
        ref={ref}
        onClick={onClick}
    >
        <Calendar />
        <span className="datepicker__date-frame-text">{value}</span>
        <span
            className={`${className ? 'datepicker__date-frame--isOpen' : ''}`}
        >
            <Vector />
        </span>
    </div>
));
